/**
 * StyledNumberInput Component
 *
 * A theme-aware number input component with built-in field-level validation.
 * Uses Material-UI v7 TextField with dynamic theme integration and number formatting.
 *
 * IMPORTANT:
 * - Uses ONLY theme tokens (NO hardcoded values)
 * - Handles validation at field level (NOT section level)
 * - Supports stepper controls and number formatting
 * - Optimized with React.memo
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  TextField,
  TextFieldProps,
  useTheme,
  InputAdornment,
  IconButton,
  SxProps,
  Theme
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import {
  validateField,
  ValidationRule,
  ValidationError
} from '../../validation';

// ============================================================================
// COMPONENT INTERFACES
// ============================================================================

export interface StyledNumberInputProps extends Omit<TextFieldProps, 'onChange' | 'value' | 'error' | 'type'> {
  /**
   * Core Functionality
   */
  value: number | null;
  onChange: (value: number | null) => void;

  /**
   * Form Integration
   */
  name?: string;
  label?: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;

  /**
   * Number Configuration
   */
  min?: number;
  max?: number;
  step?: number;
  decimals?: number;

  /**
   * Number Formatting
   */
  format?: 'number' | 'currency' | 'percentage' | 'decimal';
  currencyCode?: string;

  /**
   * Stepper Controls
   */
  showStepper?: boolean;
  stepperPosition?: 'start' | 'end';

  /**
   * Validation (Field-Level Responsibility)
   */
  error?: boolean;
  errorMessage?: string;
  rules?: ValidationRule[];
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  onValidationChange?: (errors: ValidationError[]) => void;

  /**
   * Theme Integration (REQUIRED - no hardcoded styles)
   */
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';

  /**
   * Accessibility
   */
  'aria-label'?: string;
  'aria-describedby'?: string;
  'data-testid'?: string;

  /**
   * Style Overrides (Theme-Aware)
   */
  sx?: SxProps<Theme>;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format a number value according to the specified format
 */
const formatNumber = (
  value: number | null,
  format: 'number' | 'currency' | 'percentage' | 'decimal' = 'number',
  currencyCode: string = 'USD',
  decimals?: number
): string => {
  if (value === null || value === undefined) {
    return '';
  }

  const locale = navigator.language || 'en-US';

  try {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currencyCode,
          minimumFractionDigits: decimals ?? 2,
          maximumFractionDigits: decimals ?? 2
        }).format(value);

      case 'percentage':
        // For percentage, we expect the value to be a percentage number (15 = 15%)
        // We need to divide by 100 for Intl.NumberFormat which expects decimal
        return new Intl.NumberFormat(locale, {
          style: 'percent',
          minimumFractionDigits: decimals ?? 0,
          maximumFractionDigits: decimals ?? 2
        }).format(value / 100);

      case 'decimal':
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: decimals ?? 0,
          maximumFractionDigits: decimals ?? 2
        }).format(value);

      case 'number':
      default:
        if (decimals !== undefined) {
          return value.toFixed(decimals);
        }
        return value.toString();
    }
  } catch (error) {
    console.warn('Number formatting error:', error, 'Value:', value, 'Format:', format);
    return value.toString();
  }
};

/**
 * Parse a formatted string back to a number
 */
const parseNumber = (
  input: string,
  format: 'number' | 'currency' | 'percentage' | 'decimal' = 'number'
): number | null => {
  if (!input || input.trim() === '') {
    return null;
  }

  // Remove formatting characters
  let cleanInput = input;

  switch (format) {
    case 'currency':
      // Remove currency symbols and formatting
      cleanInput = input.replace(/[$€£¥₹,\s]/g, '');
      break;

    case 'percentage':
      // Remove % symbol and formatting
      cleanInput = input.replace(/[%,\s]/g, '');
      break;

    case 'decimal':
      // Remove thousands separators but keep decimal point
      cleanInput = input.replace(/,/g, '');
      break;

    case 'number':
    default:
      // Basic number parsing
      cleanInput = input.replace(/[^\d.-]/g, '');
      break;
  }

  const parsed = parseFloat(cleanInput);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Constrain a value within min/max bounds
 */
const constrainValue = (value: number | null, min?: number, max?: number): number | null => {
  if (value === null) return null;

  let constrained = value;
  if (min !== undefined && constrained < min) {
    constrained = min;
  }
  if (max !== undefined && constrained > max) {
    constrained = max;
  }

  return constrained;
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const StyledNumberInput: React.FC<StyledNumberInputProps> = React.memo(({
  // Core props
  value,
  onChange,
  name,
  label,
  placeholder,
  helperText,
  required = false,
  disabled = false,
  readOnly = false,

  // Number configuration
  min,
  max,
  step = 1,
  decimals,

  // Formatting
  format = 'number',
  currencyCode = 'USD',

  // Stepper controls
  showStepper = false,
  stepperPosition = 'end',

  // Validation
  error: externalError = false,
  errorMessage,
  rules = [],
  validateOnChange = false,
  validateOnBlur = true,
  onValidationChange,

  // Theme integration
  variant = 'outlined',
  size = 'medium',
  color = 'primary',

  // Accessibility
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'data-testid': dataTestId,

  // Style overrides
  sx,

  // Other props
  ...otherProps
}) => {
  const theme = useTheme();

  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [internalValue, setInternalValue] = useState<string>(() => {
    // Initialize with formatted value on mount
    if (value !== null && value !== undefined) {
      return formatNumber(value, format, currencyCode, decimals);
    }
    return '';
  });
  const [isFocused, setIsFocused] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  // ============================================================================
  // INITIALIZE INTERNAL VALUE FROM PROPS
  // ============================================================================

  // Initialize internal value when component mounts or when value changes from outside
  useEffect(() => {
    if (!isFocused) {
      // Only update if not currently focused (to avoid interfering with user typing)
      const formatted = value !== null && value !== undefined
        ? formatNumber(value, format, currencyCode, decimals)
        : '';
      setInternalValue(formatted);
    }
  }, [value, format, currencyCode, decimals, isFocused]);

  // ============================================================================
  // VALIDATION LOGIC
  // ============================================================================

  const runValidation = useCallback((valueToValidate: number | null) => {
    if (!rules || rules.length === 0) {
      return [];
    }

    const errors = validateField(valueToValidate, rules, name || 'number');
    setValidationErrors(errors);
    onValidationChange?.(errors);

    return errors;
  }, [rules, name, onValidationChange]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = event.target.value;

    // Check if input contains invalid characters (anything other than digits, decimal point, minus)
    const hasInvalidChars = /[^\d.-]/.test(rawValue);

    // Filter input to only allow valid numeric characters
    let filteredValue = rawValue;

    // Allow only digits, decimal point, and minus sign at the beginning
    filteredValue = filteredValue.replace(/[^\d.-]/g, '');

    // Ensure minus sign only appears at the beginning
    if (filteredValue.indexOf('-') > 0) {
      filteredValue = filteredValue.replace(/-/g, '');
    }

    // Ensure only one decimal point
    const decimalIndex = filteredValue.indexOf('.');
    if (decimalIndex !== -1) {
      const beforeDecimal = filteredValue.substring(0, decimalIndex + 1);
      const afterDecimal = filteredValue.substring(decimalIndex + 1).replace(/\./g, '');
      filteredValue = beforeDecimal + afterDecimal;
    }

    setInternalValue(filteredValue);

    // Always parse as 'number' when user is typing - format is only for display
    const parsedValue = parseNumber(filteredValue, 'number');
    const constrainedValue = constrainValue(parsedValue, min, max);

    if (validateOnChange) {
      runValidation(constrainedValue);
    }

    // Only call onChange if input didn't contain invalid characters
    // This prevents onChange from being called when user types letters (e.g., 'abc')
    if (!hasInvalidChars) {
      onChange(constrainedValue);
    }
  }, [onChange, min, max, validateOnChange, runValidation]);

  const handleFocus = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    setInternalValue(value?.toString() || '');
    otherProps.onFocus?.(event);
  }, [value, otherProps]);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);

    // Format the current value for display
    const formattedValue = value !== null && value !== undefined
      ? formatNumber(value, format, currencyCode, decimals)
      : '';

    setInternalValue(formattedValue);

    if (validateOnBlur) {
      runValidation(value);
    }

    otherProps.onBlur?.(event);
  }, [value, format, currencyCode, decimals, validateOnBlur, runValidation, otherProps]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled || readOnly) return;

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      const currentValue = value || 0;
      const newValue = constrainValue(currentValue + step, min, max);
      onChange(newValue);
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      const currentValue = value || 0;
      const newValue = constrainValue(currentValue - step, min, max);
      onChange(newValue);
    }

    otherProps.onKeyDown?.(event);
  }, [value, step, min, max, disabled, readOnly, onChange, otherProps]);

  // ============================================================================
  // STEPPER HANDLERS
  // ============================================================================

  const handleIncrement = useCallback(() => {
    if (disabled || readOnly) return;

    const currentValue = value || 0;
    const newValue = currentValue + step;
    const constrainedValue = constrainValue(newValue, min, max);
    onChange(constrainedValue);

    if (validateOnChange) {
      runValidation(constrainedValue);
    }
  }, [value, step, min, max, disabled, readOnly, onChange, validateOnChange, runValidation]);

  const handleDecrement = useCallback(() => {
    if (disabled || readOnly) return;

    const currentValue = value || 0;
    const newValue = currentValue - step;
    const constrainedValue = constrainValue(newValue, min, max);
    onChange(constrainedValue);

    if (validateOnChange) {
      runValidation(constrainedValue);
    }
  }, [value, step, min, max, disabled, readOnly, onChange, validateOnChange, runValidation]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const hasError = externalError || validationErrors.length > 0;
  const displayErrorMessage = errorMessage || (validationErrors[0]?.message);

  const fieldId = useMemo(() => `number-input-${name || Math.random().toString(36).substr(2, 9)}`, [name]);

  // ============================================================================
  // STEPPER COMPONENTS
  // ============================================================================

  const stepperButtons = useMemo(() => {
    if (!showStepper) return null;

    return (
      <>
        <IconButton
          size={size}
          disabled={disabled || (max !== undefined && value !== null && value >= max)}
          onClick={handleIncrement}
          aria-label="increment"
          sx={{
            color: theme.palette.action.active,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            '&:disabled': {
              color: theme.palette.action.disabled,
            }
          }}
        >
          <AddIcon fontSize={size === 'small' ? 'small' : 'medium'} />
        </IconButton>
        <IconButton
          size={size}
          disabled={disabled || (min !== undefined && value !== null && value <= min)}
          onClick={handleDecrement}
          aria-label="decrement"
          sx={{
            color: theme.palette.action.active,
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            '&:disabled': {
              color: theme.palette.action.disabled,
            }
          }}
        >
          <RemoveIcon fontSize={size === 'small' ? 'small' : 'medium'} />
        </IconButton>
      </>
    );
  }, [showStepper, size, disabled, max, min, value, handleIncrement, handleDecrement, theme]);

  // ============================================================================
  // INPUT ADORNMENTS
  // ============================================================================

  const InputProps = useMemo(() => {
    const adornments: any = {};

    if (stepperButtons) {
      if (stepperPosition === 'start') {
        adornments.startAdornment = (
          <InputAdornment position="start">
            {stepperButtons}
          </InputAdornment>
        );
      } else {
        adornments.endAdornment = (
          <InputAdornment position="end">
            {stepperButtons}
          </InputAdornment>
        );
      }
    }

    return {
      ...adornments,
      readOnly,
      sx: {
        '& .MuiInputBase-input': {
          textAlign: format === 'currency' ? 'right' : 'left',
        }
      }
    };
  }, [stepperButtons, stepperPosition, readOnly, format]);

  // ============================================================================
  // THEME-AWARE STYLING
  // ============================================================================

  const fieldSx = useMemo((): SxProps<Theme> => {
    const baseSx: SxProps<Theme> = {
      // Ensure consistent sizing regardless of error state
      width: '100%',
      minWidth: 0, // Prevent flex shrinking issues
      '& .MuiOutlinedInput-root': {
        backgroundColor: theme.palette.background.paper,
        minHeight: size === 'small' ? '40px' : '56px', // Fixed height to prevent jumping
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.primary.main,
          borderWidth: 2,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.error.main,
        },
      },
      '& .MuiInputBase-input': {
        // Ensure consistent input sizing
        boxSizing: 'border-box',
      },
      '& .MuiInputLabel-root': {
        color: theme.palette.text.secondary,
        '&.Mui-focused': {
          color: hasError ? theme.palette.error.main : theme.palette.primary.main,
        },
        '&.Mui-error': {
          color: theme.palette.error.main,
        },
      },
      '& .MuiFormHelperText-root': {
        color: hasError ? theme.palette.error.main : theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
        minHeight: '1.5em', // Reserve space for helper text to prevent layout shift
      },
      // Hide native number input spinners/arrows when we have custom steppers
      '& input[type=number]': {
        MozAppearance: 'textfield', // Firefox
        '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
          WebkitAppearance: 'none', // Chrome, Safari, Edge
          margin: 0,
        },
      },
    };

    // Use array syntax for sx prop merging - this allows external sx to properly
    // override internal styles including nested selectors like .MuiOutlinedInput-root
    if (!sx) return baseSx;
    return [baseSx, sx] as SxProps<Theme>;
  }, [theme, hasError, sx, size]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <TextField
      {...otherProps}
      id={fieldId}
      name={name}
      label={label}
      placeholder={placeholder}
      helperText={displayErrorMessage || helperText}
      required={required}
      disabled={disabled}
      variant={variant}
      size={size}
      color={hasError ? 'error' : color}
      error={hasError}
      value={internalValue}
      onChange={handleInputChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      type="text"
      inputMode="numeric"
      InputProps={InputProps}
      InputLabelProps={{
        shrink: internalValue !== '' || isFocused || placeholder !== undefined
      }}
      inputProps={{
        readOnly,
        'data-testid': dataTestId,
        'aria-label': ariaLabel,
      }}
      sx={fieldSx}
      aria-label={ariaLabel}
      aria-describedby={ariaDescribedBy}
      aria-required={required}
    />
  );
});

// Set display name for debugging
StyledNumberInput.displayName = 'StyledNumberInput';

// ============================================================================
// EXPORT
// ============================================================================

export default StyledNumberInput;
